import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useAppContext } from "../../../../AppContext";
import { Typography } from "../../../../ui/Typography/Typography";
import { Button } from "../../../../ui/Button/Button";
import { Chip } from "../../../../ui/Chip/Chip";
import { Modal } from "../../../../ui/Modal/Modal";
import classes from "./ActiveSubscription.module.scss";
import { SettingStep } from "../../../../stores/SettingsStore";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";

type ActiveSubscriptionProps = {
  token: string | undefined;
  mobile: boolean | undefined;
};

export const ActiveSubscription = observer((props: ActiveSubscriptionProps) => {
  const {
    store: { settingsStore, subscriptionPlanStore },
  } = useAppContext();

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Загружаем тарифные планы при старте компонента
  useEffect(() => {
    subscriptionPlanStore.loadSubscriptionPlans(props.token);
  }, [subscriptionPlanStore, props.token]);
  
  const navigate = useNavigate();

  // Получаем активные планы
  const activePlans = subscriptionPlanStore.getActivePlans();

  // Используем первый активный план или данные из settingsStore как fallback
  const activePlan = activePlans.length > 0 ? activePlans.find(i => i.id === settingsStore.ynaProActivePlanId) : null;
  const currentBilling = subscriptionPlanStore.myBillings?.find(i => i.subscriptionPlan?.id == activePlan?.id);
  
  const price = activePlan?.price ?? settingsStore.ynaProActivePrice;
  const nextPaymentDate = currentBilling?.nextPaymentDate
    ? currentBilling.nextPaymentDate
    : activePlan?.endDate
      ? activePlan.endDate
      : settingsStore.ynaProActiveNextBillingDate;
  const nextBillingDateAsString = nextPaymentDate ?
    new Date(nextPaymentDate!).toLocaleDateString("ru-RU") : null;
  const chipName = activePlan?.name ?? settingsStore.ynaProActivePlanName;
  const chipType = settingsStore.ynaProActivePlanType;

  const handleOpenConfirm = () => {
    subscriptionPlanStore.setError(null);
    setConfirmOpen(true);
  };
  
  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  }

  const handleDisable = async () => {
    try {
      if (!activePlan) {
        return;
      }

      await subscriptionPlanStore.cancelSubscription(activePlan.id, props.token);

      // После успешной отмены делаем текущую подписку неактивной и возвращаемся к списку YNA Pro
      settingsStore.setYnaProCurrentActive(false);
      settingsStore.setYnaProActivePlan();

      // Сбрасываем состояние trial, чтобы блок снова был доступен для активации
      localStorage.removeItem("yna_pro_trial_active");
      localStorage.removeItem("yna_pro_trial_pending");

      if (!props.mobile) {
        settingsStore.setStepType(SettingStep.YnaPro);
        setConfirmOpen(false);
      } else {
        navigate(`/payments-mobile/cancel-payments-mobile/${nextPaymentDate}/${encodeURIComponent(props.token!)}`);
      }
    } catch (err: any) {
      // Ошибка уже сохранена в subscriptionPlanStore.error
    } finally {
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.subscriptionActive}>
        <Typography fsz={20} fw={400}>
          Подписка активна!
        </Typography>
      </div>

      <div className={classes.info}>
        {activePlan ? (
          <>
            {activePlan.name && (
              <Typography className={classes.infoText} fw={500}>
                {activePlan.name}
              </Typography>
            )}
            {activePlan.description && (
              <Typography className={classes.infoText}>
                {activePlan.description}
              </Typography>
            )}
            {activePlan.price && (
              <Typography className={classes.infoText}>
                Стоимость - {activePlan.price} руб./мес
              </Typography>
            )}
            {nextBillingDateAsString && (
              <Typography className={classes.infoText}>
                Следующее списание -{" "}
                {nextBillingDateAsString}
              </Typography>
            )}
          </>
        ) : (
          <>
            {price && (
              <Typography className={classes.infoText}>
                Стоимость - {price} руб./мес
              </Typography>
            )}
            {nextBillingDateAsString && (
              <Typography className={classes.infoText}>
                Следующее списание - {nextBillingDateAsString}
              </Typography>
            )}
          </>
        )}
      </div>

      <div className={classes.cancelButtonWrapper}>
        <Button
          buttonType="secondary"
          buttonSize="md"
          onClick={handleOpenConfirm}
        >
          Отменить подписку
        </Button>
      </div>

      <Typography className={classes.disclaimer}>
        После отмены функции подписки будут доступны до конца оплаченного
        периода.
      </Typography>

      <Modal adaptive open={confirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <Stack direction="column" gap={10} alignItems="center">
            <Typography fsz={16} fw={400}>
              Вы точно хотите отменить подписку?
            </Typography>
            {subscriptionPlanStore.error && (
              <Typography fsz={14} fw={400} style={{ color: "#ff3147" }}>
                {subscriptionPlanStore.error}
              </Typography>
            )}
            {chipName && chipType && (
              <div className={classes.confirmChipWrapper}>
                <Chip size="sm" type={chipType}>
                  {chipName}
                </Chip>
              </div>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            gap={24}
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Button
              buttonType="primary"
              buttonSize="md"
              onClick={handleCloseConfirm}
              disabled={subscriptionPlanStore.isLoading}
            >
              Отмена
            </Button>
            <Button
              buttonType="secondary"
              buttonSize="md"
              onClick={handleDisable}
              disabled={subscriptionPlanStore.isLoading}
            >
              {subscriptionPlanStore.isLoading ? "Отключение..." : "Отключить"}
            </Button>
          </Stack>
        </DialogActions>
      </Modal>
    </div>
  );
});
